import React, { useState } from 'react';
import API_BASE_URL from '../../../../js/urlHelper';
import { fetchWithAuth } from '../../../../js/authToken';
import { toast } from 'react-toastify';
import LoadingScreen from '../../../Reutilizables/FetchWithGif';
import ClienteSearch from './components/ClienteSearch';
import PrestamosList from './components/PrestamosList';
import PagoCuotaModal from './components/modals/PagoCuotaModal';
import CancelarPrestamoModal from './components/modals/CancelarPrestamoModal';
import CapturaModal from './components/modals/CapturaModal';
import ComprobanteModal from './components/modals/ComprobanteModal';
import ConfirmationModal from './components/modals/ConfirmationModal';
import ComprobantesParcialesModal from './components/modals/ComprobantesParcialesModal';
import { format, parseISO } from 'date-fns';

export default function PagoComponent() {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [cuotasPendientes, setCuotasPendientes] = useState([]);
  const [selectedCuota, setSelectedCuota] = useState(null);
  const [selectedPrestamo, setSelectedPrestamo] = useState(null);
  const [formData, setFormData] = useState({
    idCuota: '',
    idCliente: '',
    monto_pagado: '',
    numero_operacion: '',
    observaciones: ''
  });
  const [cancelarForm, setCancelarForm] = useState({
    idPrestamo: '',
    idCliente: '',
    monto_pagado: '',
    numero_operacion: '',
    observaciones: ''
  });
  const [showCuotaModal, setShowCuotaModal] = useState(false);
  const [showCapturaModal, setShowCapturaModal] = useState(false);
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  const [showComprobanteModal, setShowComprobanteModal] = useState(false);
  const [modalContent, setModalContent] = useState({ capturaUrl: '', comprobanteUrl: '' });
  const [modalTitle, setModalTitle] = useState('');
  const [modalError, setModalError] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [errors, setErrors] = useState({});
  const [showComprobantesParcialesModal, setShowComprobantesParcialesModal] = useState(false);
  const [pagosMultiples, setPagosMultiples] = useState({ prestamo: null, cuota: null, pagos: [] });
  const [loadingComprobantes, setLoadingComprobantes] = useState(false);

  const ensureHttpsUrl = (url) => {
    if (!url) return url;
    if (url.includes('localhost') || url.includes('127.0.0.1')) {
      return url;
    }
    if (url.startsWith('http://')) {
      return url.replace('http://', 'https://');
    }
    return url;
  };

  const resetSelection = () => {
    setSelectedCliente(null);
    setCuotasPendientes([]);
    setSelectedCuota(null);
    setSelectedPrestamo(null);
    setShowCuotaModal(false);
    setShowCancelarModal(false);
    setShowConfirmationModal(false);
    setShowCapturaModal(false);
    setShowComprobanteModal(false);
    setErrors({});
    setModalContent({ capturaUrl: '', comprobanteUrl: '' });
    setModalTitle('');
    setModalError('');
  };

  const handleVerComprobantesMultiples = async (prestamo, cuota, pagosAplicados) => {
    setLoadingComprobantes(true);
    setShowComprobantesParcialesModal(true);
    try {
      const pagosConComprobantes = await Promise.all(
        pagosAplicados.map(async (pago) => {
          try {
            const response = await fetchWithAuth(
              `${API_BASE_URL}/api/cajero/pagos/comprobante-por-pago/${pago.idPago}`,
              { method: 'GET' }
            );
            const data = await response.json();
            if (!response.ok) {
              throw new Error(data.message || 'Error al obtener comprobante');
            }
            return {
              ...pago,
              comprobante_url: ensureHttpsUrl(data.comprobante_url)
            };
          } catch (error) {
            console.error(`Error al obtener comprobante para pago ${pago.idPago}:`, error);
            return {
              ...pago,
              comprobante_url: null
            };
          }
        })
      );

      setPagosMultiples({
        prestamo,
        cuota,
        pagos: pagosConComprobantes
      });
    } catch (error) {
      console.error('Error al cargar comprobantes:', error);
      toast.error('Error al cargar los comprobantes de pago');
    } finally {
      setLoadingComprobantes(false);
    }
  };


  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Ingrese un criterio de búsqueda');
      return;
    }

    resetSelection();
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/cajero/pagos/buscar-clientes?query=${encodeURIComponent(searchQuery)}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al buscar clientes');
      }

      setSearchResults(data.data || []);
      if (data.data && data.data.length === 0) {
        toast.info('No se encontraron clientes con ese criterio');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al buscar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCliente = async (cliente) => {
    setSelectedCliente(cliente);
    setSearchResults([]);
    setSearchQuery(`${cliente.nombre} ${cliente.apellidoPaterno} ${cliente.apellidoMaterno} - ${cliente.dni}`);
    setLoading(true);

    try {
      const timestamp = new Date().getTime();
      
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/cajero/pagos/cuotas-pendientes/${cliente.idUsuario}?_t=${timestamp}`,
        { 
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al obtener cuotas pendientes');
      }

      if (!data.success) {
        toast.info(data.message || 'El cliente no tiene cuotas pendientes');
        setCuotasPendientes([]);
        return;
      }

      setCuotasPendientes(data.data.prestamos || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al obtener cuotas pendientes');
      setCuotasPendientes([]);
    } finally {
      setLoading(false);
    }
  };

  const validateCancelForm = () => {
    const newErrors = {};
    if (!cancelarForm.monto_pagado || parseFloat(cancelarForm.monto_pagado) <= 0) {
      newErrors.monto_pagado = 'Ingrese un monto válido';
    }
    if (!cancelarForm.numero_operacion.trim()) {
      newErrors.numero_operacion = 'El número de operación es obligatorio';
    } else if (!/^\d+$/.test(cancelarForm.numero_operacion.trim())) {
      newErrors.numero_operacion = 'El número de operación debe contener solo números';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePagarCuota = (prestamo, cuota) => {
    setSelectedPrestamo(prestamo);
    setSelectedCuota(cuota);
    const montoAPagar = (() => {
      const saldo = parseFloat(cuota.saldo_pendiente ?? NaN);
      if (!Number.isNaN(saldo)) return Math.max(0, saldo);
      const base = parseFloat(cuota.monto_a_pagar ?? cuota.monto ?? 0) || 0;
      const excedente = parseFloat(cuota.excedente_anterior ?? 0) || 0;
      return Math.max(0, base - excedente);
    })();
    setFormData({
      idCuota: cuota.idCuota,
      idCliente: selectedCliente.idUsuario,
      monto_pagado: montoAPagar.toFixed(2),
      numero_operacion: '',
      observaciones: ''
    });
    setShowCuotaModal(true);
  };

  // Ver captura de pago
  const handleVerCaptura = async (prestamo, cuota) => {
    setSelectedPrestamo(prestamo);
    setSelectedCuota(cuota);
    setLoading(true);
    setModalContent({ capturaUrl: '', comprobanteUrl: '' });
    setModalError('');
    setModalTitle(`Captura de Pago - Cuota #${cuota.numero_cuota}`);

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/cajero/pagos/captura/${cuota.idCuota}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al obtener captura de pago');
      }

      const capturaUrl = data.capturapago_url ? ensureHttpsUrl(data.capturapago_url) : '';
      if (!capturaUrl) {
        throw new Error('No hay captura de pago disponible');
      }

      setModalContent({ capturaUrl, comprobanteUrl: '' });
      setShowCapturaModal(true);
    } catch (error) {
      console.error('Error:', error);
      setModalError(error.message || 'Error al obtener captura de pago');
      setShowCapturaModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Ver comprobante de pago
  const handleVerComprobante = async (prestamo, cuota) => {
    setSelectedPrestamo(prestamo);
    setSelectedCuota(cuota);
    setLoading(true);
    setModalContent({ capturaUrl: '', comprobanteUrl: '' });
    setModalError('');
    setModalTitle(`Comprobante de Pago - Cuota #${cuota.numero_cuota}`);

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/cajero/pagos/comprobante/${cuota.idCuota}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al obtener comprobante de pago');
      }

      const comprobanteUrl = data.comprobante_url ? ensureHttpsUrl(data.comprobante_url) : '';
      if (!comprobanteUrl) {
        throw new Error('No hay comprobante de pago disponible');
      }

      setModalContent({ capturaUrl: '', comprobanteUrl });
      setShowComprobanteModal(true);
    } catch (error) {
      console.error('Error:', error);
      setModalError(error.message || 'Error al obtener comprobante de pago');
      setShowComprobanteModal(true);
    } finally {
      setLoading(false);
    }
  };
  const handleCancelarPrestamo = (prestamo) => {
    // Calcular excedentes disponibles de todo el préstamo
    const excedentesDisponibles = prestamo.cuotas
      .filter(cuota => ['pagado', 'prepagado'].includes(cuota.estado))
      .reduce((total, cuota) => {
        const pagosAplicados = Array.isArray(cuota.pagos) ? cuota.pagos : [];
        const excedentesCuota = pagosAplicados.reduce((acc, pago) => {
          const excedente = parseFloat(pago.excedente ?? 0) || 0;
          return acc + excedente;
        }, 0);
        return total + excedentesCuota;
      }, 0);

    // Calcular monto pendiente de todas las cuotas sin aplicar excedentes aún
    const montoPendienteSinExcedentes = prestamo.cuotas
      .filter(cuota => !['pagado', 'prepagado'].includes(cuota.estado))
      .reduce((total, cuota) => {
        // Priorizar saldo_pendiente si está disponible
        const saldo = parseFloat(cuota.saldo_pendiente ?? NaN);
        if (!Number.isNaN(saldo)) {
          return total + Math.max(0, saldo);
        }

        // Calcular el saldo pendiente considerando pagos parciales
        const montoCuota = parseFloat(cuota.monto ?? 0) || 0;
        const moraActual = parseFloat(cuota.mora ?? 0) || 0;
        const excedenteAnterior = parseFloat(cuota.excedente_anterior ?? 0) || 0;
        
        // Calcular total pagado de esta cuota
        const pagosAplicados = Array.isArray(cuota.pagos) ? cuota.pagos : [];
        const totalPagado = pagosAplicados.reduce((acc, pago) => {
          const pagoACuota = parseFloat(pago.pago_aplicado_cuota ?? 0) || 0;
          const pagoAMora = parseFloat(pago.pago_aplicado_mora ?? 0) || 0;
          return acc + pagoACuota + pagoAMora;
        }, 0);
        
        // Total a pagar = (cuota + mora - excedente) - pagos realizados
        const montoTotalOriginal = montoCuota + moraActual - excedenteAnterior;
        const saldoPendiente = Math.max(0, montoTotalOriginal - totalPagado);
        
        return total + saldoPendiente;
      }, 0);

    const excedentesAplicadosPendientes = prestamo.cuotas
      .filter(cuota => !['pagado', 'prepagado'].includes(cuota.estado))
      .reduce((total, cuota) => {
        const excedenteAnterior = parseFloat(cuota.excedente_anterior ?? 0) || 0;
        return total + excedenteAnterior;
      }, 0);

    const excedentesNoAplicados = Math.max(0, excedentesDisponibles - excedentesAplicadosPendientes);
    const montoPendienteFinal = Math.max(0, montoPendienteSinExcedentes - excedentesNoAplicados);

    setSelectedPrestamo(prestamo.prestamo);
    setCancelarForm({
      idPrestamo: prestamo.prestamo.idPrestamo,
      idCliente: selectedCliente.idUsuario,
      monto_pagado: montoPendienteFinal.toFixed(2),
      numero_operacion: '',
      observaciones: ''
    });
    setShowCancelarModal(true);
  };

  const validatePaymentForm = () => {
    const newErrors = {};
    if (!formData.monto_pagado || parseFloat(formData.monto_pagado) <= 0) {
      newErrors.monto_pagado = 'Ingrese un monto válido';
    }
    if (!formData.idCliente) {
      newErrors.idCliente = 'Cliente no seleccionado';
    }
    if (!formData.numero_operacion.trim()) {
      newErrors.numero_operacion = 'El número de operación es obligatorio';
    } else if (!/^\d+$/.test(formData.numero_operacion.trim())) {
      newErrors.numero_operacion = 'El número de operación debe contener solo números';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Confirmación de acción
  const handleConfirmAction = (action) => {
    let isValid = false;
    if (action === 'pagar-cuota') {
      isValid = validatePaymentForm();
    } else if (action === 'cancelar-prestamo') {
      isValid = validateCancelForm();
    }
    if (isValid) {
      setConfirmationAction(action);
      setShowConfirmationModal(true);
    }
  };

  const registrarPagoCuota = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/cajero/pagos/cuota`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Error al registrar el pago');
      }

      let estadoVerificado = false;
      const maxIntentos = 5;
      const estadosValidos = ['pendiente', 'pagado', 'vence_hoy', 'vencido', 'prepagado'];
      
      if (data.data && data.data.cuota) {
        const estadoCuota = data.data.cuota.estado;
        if (estadosValidos.includes(estadoCuota)) {
          estadoVerificado = true;
        }
      }

      if (!estadoVerificado) {
        for (let intento = 1; intento <= maxIntentos && !estadoVerificado; intento++) {
          await new Promise(resolve => setTimeout(resolve, 500 * intento));
          
          try {
            const verificacionResponse = await fetchWithAuth(
              `${API_BASE_URL}/api/cajero/pagos/cuotas-pendientes/${selectedCliente.idUsuario}`,
              { 
                method: 'GET',
                headers: { 
                  'Cache-Control': 'no-cache, no-store, must-revalidate',
                  'Pragma': 'no-cache',
                  'Expires': '0'
                }
              }
            );
            
            const verificacionData = await verificacionResponse.json();
            
            if (verificacionResponse.ok && verificacionData.success) {
              const prestamoActualizado = verificacionData.data.prestamos?.find(
                p => p.prestamo.idPrestamo === selectedPrestamo.idPrestamo
              );
              
              const cuotaActualizada = prestamoActualizado?.cuotas?.find(
                c => c.idCuota === formData.idCuota
              );
              
              if (cuotaActualizada && estadosValidos.includes(cuotaActualizada.estado)) {
                estadoVerificado = true;
                break;
              }
            }
          } catch (verifyError) {
            console.error(`Error en verificación intento ${intento}:`, verifyError);
          }
        }
        
        if (!estadoVerificado) {
          toast.warning(
            'Pago registrado, pero hay inconsistencias. Por favor, verifique el estado manualmente.',
            { autoClose: 8000 }
          );
        }
      }

      toast.success('Pago registrado correctamente');
      
      if (data.data && data.data.comprobante_url) {
        const comprobanteUrl = ensureHttpsUrl(data.data.comprobante_url);
        setModalContent({ capturaUrl: '', comprobanteUrl });
        setModalTitle(`Comprobante de Pago - Cuota #${selectedCuota.numero_cuota}`);
        setModalError('');
        setShowComprobanteModal(true);
      }

      setShowCuotaModal(false);
      setShowConfirmationModal(false);
      
      if (selectedCliente) {
        await handleSelectCliente(selectedCliente);
      }
      
    } catch (error) {
      console.error('Error al registrar pago:', error);
      toast.error(error.message || 'Error al registrar el pago');
    } finally {
      setLoading(false);
    }
  };

  const registrarCancelacionPrestamo = async () => {
    if (!cancelarForm.idCliente && !selectedCliente?.idUsuario) {
      toast.error('No se ha seleccionado un cliente válido');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ...cancelarForm,
        idCliente: cancelarForm.idCliente || selectedCliente.idUsuario
      };

      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/cajero/pagos/cancelacion-total`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Error al cancelar el préstamo');
      }

      toast.success('Préstamo cancelado correctamente');
      if (data.data && data.data.comprobante_url) {
        const comprobanteUrl = ensureHttpsUrl(data.data.comprobante_url);
        setModalContent({ capturaUrl: '', comprobanteUrl });
        setModalTitle(`Comprobante de Cancelación Total - Préstamo #${selectedPrestamo.idPrestamo}`);
        setModalError('');
        setShowComprobanteModal(true);
      }

      setShowCancelarModal(false);
      setShowConfirmationModal(false);
      if (selectedCliente) {
        await handleSelectCliente(selectedCliente);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al cancelar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (confirmationAction === 'pagar-cuota') {
      registrarPagoCuota();
    } else if (confirmationAction === 'cancelar-prestamo') {
      registrarCancelacionPrestamo();
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedCliente(null);
    setCuotasPendientes([]);
    setSelectedCuota(null);
    setSelectedPrestamo(null);
    setFormData({
      idCuota: '',
      idCliente: '',
      monto_pagado: '',
      numero_operacion: '',
      observaciones: ''
    });
    setCancelarForm({
      idPrestamo: '',
      idCliente: '',
      monto_pagado: '',
      numero_operacion: '',
      observaciones: ''
    });
    setErrors({});
    setModalContent({ capturaUrl: '', comprobanteUrl: '' });
    setModalTitle('');
    setModalError('');
  };

  const handleInputChange = (e, formType) => {
    const { name, value } = e.target;
    if (formType === 'cuota' || !formType) {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (formType === 'cancelar') {
      setCancelarForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const isCuotaPagable = (prestamo, cuota) => {
    const sortedCuotas = prestamo.cuotas.sort((a, b) => a.numero_cuota - b.numero_cuota);
    const previousCuotas = sortedCuotas.filter(c => c.numero_cuota < cuota.numero_cuota);
    const allPreviousPaid = previousCuotas.every(c => c.estado === 'pagado');
    const isCurrentUnpaid = !['pagado', 'prepagado'].includes(cuota.estado);
    return allPreviousPaid && isCurrentUnpaid;
  };

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return '-';
    }
  };

  const handleCloseCapturaModal = () => {
    setShowCapturaModal(false);
    setModalContent({ capturaUrl: '', comprobanteUrl: '' });
    setModalTitle('');
    setModalError('');
    setSelectedCuota(null);
  };

  const handleCloseComprobanteModal = () => {
    setShowComprobanteModal(false);
    setModalContent({ capturaUrl: '', comprobanteUrl: '' });
    setModalTitle('');
    setModalError('');
    setSelectedCuota(null);
  };

  return (
    <div className="w-full h-full overflow-auto pb-16">
      {loading && <LoadingScreen />}

      <div className="ui-card mx-auto w-full p-4 sm:p-6 md:p-8">
        <div className="mb-5 border-b border-slate-200 pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-red-600">Gestión de pagos</p>
          <h2 className="mt-2 text-xl font-semibold text-slate-900 sm:text-2xl">Registro de cuotas de préstamos</h2>
        </div>

        <ClienteSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          handleReset={handleReset}
          searchResults={searchResults}
          handleSelectCliente={handleSelectCliente}
        />

        {selectedCliente && searchResults.length === 0 && (
          <PrestamosList
            selectedCliente={selectedCliente}
            cuotasPendientes={cuotasPendientes}
            isCuotaPagable={isCuotaPagable}
            formatDate={formatDate}
            handlePagarCuota={handlePagarCuota}
            handleVerCaptura={handleVerCaptura}
            handleCancelarPrestamo={handleCancelarPrestamo}
            handleVerComprobante={handleVerComprobante}
            handleVerComprobantesMultiples={handleVerComprobantesMultiples}
          />
        )}
      </div>

      <PagoCuotaModal
        show={showCuotaModal}
        onClose={() => setShowCuotaModal(false)}
        selectedCuota={selectedCuota}
        selectedPrestamo={selectedPrestamo}
        formData={formData}
        errors={errors}
        handleInputChange={handleInputChange}
        handleConfirmAction={() => handleConfirmAction('pagar-cuota')}
        formatDate={formatDate}
      />

      <CancelarPrestamoModal
        show={showCancelarModal}
        onClose={() => setShowCancelarModal(false)}
        selectedPrestamo={selectedPrestamo}
        cancelarForm={cancelarForm}
        errors={errors}
        handleInputChange={(e) => handleInputChange(e, 'cancelar')}
        handleConfirmAction={() => handleConfirmAction('cancelar-prestamo')}
      />

      <CapturaModal
        show={showCapturaModal}
        onClose={handleCloseCapturaModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        modalError={modalError}
        selectedCuota={selectedCuota}
        selectedCliente={selectedCliente}
        selectedPrestamo={selectedPrestamo}
      />

      <ComprobanteModal
        show={showComprobanteModal}
        onClose={handleCloseComprobanteModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        modalError={modalError}
        selectedCuota={selectedCuota}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        confirmationAction={confirmationAction}
        formData={formData}
        cancelarForm={cancelarForm}
        handleConfirm={handleConfirm}
      />

      <ComprobantesParcialesModal
        show={showComprobantesParcialesModal}
        onClose={() => setShowComprobantesParcialesModal(false)}
        cuota={pagosMultiples.cuota}
        prestamo={pagosMultiples.prestamo}
        pagos={pagosMultiples.pagos}
        loading={loadingComprobantes}
      />
    </div>
  );
}


